// --------------------- Import Globais ---------------------
import { useEffect, useState } from "react";
import { NumberParam, StringParam, useQueryParams } from "use-query-params";

// --------------------- Import Antd-Design ---------------------
import {
  Col,
  Flex,
  Button,
  Modal,
  message,
  Row,
  MenuProps,
  Skeleton,
  Table,
  Typography,
} from "antd";
import type { TableProps } from "antd/es/table";

// --------------------- Import Material Icon ---------------------
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import HomeIcon from "@mui/icons-material/Home";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import { useMediaQuery } from "@mui/material";

// --------------------- Import Styles ---------------------
import { Container } from "./styles";

// --------------------- Import Components ---------------------
import { Columns } from "./components/Columns";
import ModalCompletarCotas from "./components/ModalCompletarCotas";
import ModalAndamentos from "./components/ModalAndamentos";
import ModalEditar from "./components/ModalEditar";
import { usePermissao } from "../../uteis/permissaoAdm/usePermissao";

// --------------------- Import Hooks ---------------------
import { useBuscarCotas } from "./hooks/useBuscarCotas";
import { useResumoCotasUsuariosEmpreendimentos } from "../../hooks/useResumoCotasUsuariosEmpreendimentos";
import { excluirCotaService } from "../../services/excluirCotaService";

export default function ViewGerenciarCota() {
  // Recupera os valores enviado pela URL (id do apartamento, nome empreendimento e nome apartamento)
  const [queryParams] = useQueryParams({
    aptoId: NumberParam,
    empreendId: NumberParam,
    empreendNome: StringParam,
    aptoNome: StringParam,
  });
  const aptoId = queryParams.aptoId;
  const empreendId = queryParams.empreendId;
  const empreendNome = queryParams.empreendNome;
  const aptoNome = queryParams.aptoNome;

  // Definições para mobile
  const isMobile = useMediaQuery("(max-width:600px)");
  // Controle de Paginação
  const [currentPage, setCurrentPage] = useState(1);
  // Traz o estilo Title da biblioteca
  const { Title } = Typography;

  // Armazena o valor da origem do apartamento
  const [cotaId, setCotaId] = useState<number | null>(null);
  // Armazena o valor da origem do apartamento
  const [numeroCota, setNumeroCota] = useState<number | null>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<Array<string | number>>(
    []
  );
  const [deletedCotaIds, setDeletedCotaIds] = useState<number[]>([]);
  const [isModalCompletarOpen, setIsModalCompletarOpen] = useState(false);
  const [isModalExcluirOpen, setIsModalExcluirOpen] = useState(false);
  const [isModalEditarOpen, setIsModalEditarOpen] = useState(false);
  const [isModalAndamentosOpen, setIsModalAndamentosOpen] = useState(false);
  const [isExcluindo, setIsExcluindo] = useState(false);

  // Inicializando o filtro
  const [semFiltros, setSemFiltros] = useState(true);

  const [filteredInfo, setFilteredInfo] = useState<{
    numeroCota?: string[] | null;
    origem?: string[] | null;
  }>({
    numeroCota: null,
    origem: null,
  });
  const [messageApi, contextHolder] = message.useMessage();

  // Verificação de permissaão
  const { isLoading: isTokenLoading, isValid } = usePermissao("OPE");

  // Busca as cotas do apartamento
  const { cotas, isLoading } = useBuscarCotas(aptoId);
  const {
    resumosPorEmpreendimento,
    isLoading: isLoadingResumoCotasUsuarios,
  } = useResumoCotasUsuariosEmpreendimentos([empreendId]);
  const resumoApartamento = empreendId
    ? resumosPorEmpreendimento[empreendId]?.apartamentos.find(
        (apartamento) => apartamento.id === aptoId
      )
    : undefined;
  const cotasComResumo = cotas
    .filter((cota: any) => !deletedCotaIds.includes(Number(cota.key)))
    .map((cota: any) => ({
      ...cota,
      cotasUsuarios:
        resumoApartamento?.cotas.find((item) => item.id === Number(cota.key))
          ?.totalActiveCotasUsuarios ?? 0,
    }));

  // UseEffect armazenamento do filtro
  useEffect(() => {
    const filtros = localStorage.getItem("filtrosPropriedades");
    const pagina = localStorage.getItem("pageGerPropriedades");

    if (filtros) {
      const novoFiltro = JSON.parse(filtros);
      setFilteredInfo({
        numeroCota: novoFiltro.numeroCota ? [novoFiltro.numeroCota] : null,
        origem: novoFiltro.origem ? [novoFiltro.origem] : null,
      });
      setSemFiltros(false);
    }

    if (pagina) {
      setCurrentPage(Number(pagina));
    }
  }, []);

  const onChange: TableProps<any>["onChange"] = (pagination, filters) => {
    // Atualiza o estado filteredInfo com os filtros aplicados nas colunas 'numeroCota' e 'origem'.
    // Caso não haja filtros, o valor será null.
    setFilteredInfo({
      numeroCota: filters.numeroCota as string[] | null,
      origem: filters.origem as string[] | null,
    });

    // Verifica se há algum filtro ativo (numeroCota ou origem) e atualiza o estado semFiltros.
    // Caso não haja filtros, semFiltros será true.
    const hasFilters = Boolean(filters.numeroCota || filters.origem);
    setSemFiltros(!hasFilters);

    // Se houver paginação ativa, armazena o número da página atual no localStorage.
    if (pagination.current) {
      localStorage.setItem(
        "pageGerPropriedades", // Nome da chave no localStorage
        pagination.current.toString() // Salva a página atual como string.
      );
    }
    // Armazena todos os filtros aplicados no localStorage, convertidos para JSON.
    localStorage.setItem("filtrosPropriedades", JSON.stringify(filters));
  };

  // Remove os filtros aplicados
  const removerFiltros = () => {
    localStorage.removeItem("filtrosPropriedades");
    localStorage.removeItem("pageGerPropriedades");

    setFilteredInfo({ numeroCota: null, origem: null });
    setSemFiltros(true);
    setCurrentPage(1);
  };

  // Funçao q ativa a remoção dos filtros
  const handleFilter = () => {
    removerFiltros();
  };

  const showSuccess = (texto: string) => {
    messageApi.open({
      type: "success",
      content: texto,
      key: "success",
    });
  };

  const showError = (texto: string) => {
    messageApi.open({
      type: "error",
      content: texto,
      key: "error",
    });
  };

  const abrirModalExcluir = () => {
    if (selectedRowKeys.length === 0) {
      return;
    }

    setIsModalExcluirOpen(true);
  };

  const abrirModalCompletar = () => {
    setIsModalCompletarOpen(true);
  };

  const fecharModalExcluir = () => {
    if (!isExcluindo) {
      setIsModalExcluirOpen(false);
    }
  };

  const fecharModalCompletar = () => {
    setIsModalCompletarOpen(false);
  };

  const abrirModalEditar = () => {
    setIsModalEditarOpen(true);
  };

  const fecharModalEditar = () => {
    setIsModalEditarOpen(false);
  };

  const abrirModalAndamentos = () => {
    setIsModalAndamentosOpen(true);
  };

  const fecharModalAndamentos = () => {
    setIsModalAndamentosOpen(false);
  };

  const excluirSelecionadas = async () => {
    if (selectedRowKeys.length === 0) {
      return;
    }

    setIsExcluindo(true);

    try {
      const idsSelecionados = selectedRowKeys.map((cotaSelecionadaId) =>
        Number(cotaSelecionadaId)
      );
      const resultados = await Promise.allSettled(
        idsSelecionados.map((cotaSelecionadaId) =>
          excluirCotaService(Number(cotaSelecionadaId))
        )
      );
      const idsExcluidos = idsSelecionados.filter(
        (_, index) => resultados[index]?.status === "fulfilled"
      );
      const idsComFalha = idsSelecionados.filter(
        (_, index) => resultados[index]?.status === "rejected"
      );

      const sucessos = resultados.filter(
        (resultado) => resultado.status === "fulfilled"
      ).length;
      const falhas = resultados.length - sucessos;

      if (sucessos > 0) {
        showSuccess(
          `${sucessos} cota${sucessos > 1 ? "s" : ""} excluida${
            sucessos > 1 ? "s" : ""
          } com sucesso.`
        );
      }

      if (falhas > 0) {
        showError(
          `${falhas} cota${falhas > 1 ? "s nao puderam" : " nao pode"} ser excluida${
            falhas > 1 ? "s" : ""
          }.`
        );
      }

      if (idsExcluidos.length > 0) {
        setDeletedCotaIds((prevState) => [
          ...prevState,
          ...idsExcluidos.filter((id) => !prevState.includes(id)),
        ]);
      }

      setSelectedRowKeys(idsComFalha);
      setIsModalExcluirOpen(false);
    } finally {
      setIsExcluindo(false);
    }
  };

  const rowSelection: TableProps<any>["rowSelection"] = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys) => {
      setSelectedRowKeys(newSelectedRowKeys);
    },
  };

  // OnClick das opções e funções que serão realizadas do item Ações
  const onClick: MenuProps["onClick"] = async ({ key }) => {
    switch (key) {
      // Gerenciar Períodos
      case "1":
        document.location.href = `/gerenciarPeriodos/?cotaId=${cotaId}&&numeroCota=${numeroCota}&&aptoNome=${aptoNome}&&empreendNome=${empreendNome}&&aptoId=${aptoId}&&empreendId=${empreendId ?? ""}`;
        break;

      // Editar
      case "2":
        abrirModalEditar();
        break;

      // Ver Andamentos
      case "3":
        abrirModalAndamentos();
        break;
    }
  };

  // Traz as informações  OnClick, cotaId e numero da cota , ao clickar na linha especifica
  const columns = Columns(onClick, setCotaId, setNumeroCota, {
    numeroCota: filteredInfo.numeroCota,
    origem: filteredInfo.origem,
  });

  return (
    <>
      {contextHolder}
      {(isLoading || isLoadingResumoCotasUsuarios || isTokenLoading) ||
      !isValid ? (
        <Skeleton active />
      ) : (
        <Container>
          <Row>
            <Col span={5}>
              <Flex align="center" justify="center" style={{ marginTop: 15 }}>
                <a
                  href={`/gerenciarApto${
                    empreendId
                      ? `?empreendId=${empreendId}&empreendNome=${encodeURIComponent(
                          empreendNome ?? ""
                        )}`
                      : ""
                  }`}
                >
                  <ArrowBackIcon color="primary" />
                </a>
              </Flex>
            </Col>
            <Col span={12}>
              <Flex align="center" justify="center">
                <HomeIcon color="primary" fontSize="large" />

                <Title
                  style={{
                    marginLeft: 10,
                    marginTop: 10,
                    fontSize: isMobile ? "1rem" : "2rem",
                    color: "rgb(0, 54, 68)",
                  }}
                  level={3}
                >
                  Gerenciar Cotas do Apartamento {aptoNome}
                </Title>
              </Flex>
            </Col>
          </Row>
          <Row>
            <Col span={24}>
              <Flex
                justify="flex-end"
                align="center"
                gap={12}
                style={{
                  marginTop: 10,
                  marginRight: isMobile ? 20 : 55,
                  marginBottom: 8,
                  flexWrap: "wrap",
                }}
              >
                <Button onClick={abrirModalCompletar} type="primary">
                  Completar cotas
                </Button>

                <Button
                  danger
                  type="primary"
                  disabled={selectedRowKeys.length === 0}
                  onClick={abrirModalExcluir}
                >
                  Excluir selecionadas
                  {selectedRowKeys.length > 0
                    ? ` (${selectedRowKeys.length})`
                    : ""}
                </Button>

                {semFiltros ? (
                  <Button onClick={handleFilter} type="primary" disabled={true}>
                    <FilterAltIcon />
                  </Button>
                ) : (
                  <Button onClick={handleFilter} type="primary">
                    <FilterAltOffIcon />
                  </Button>
                )}
              </Flex>
            </Col>
            <Col span={isMobile ? 5 : 1}></Col>
            <Col
              span={22}
              style={{
                marginTop: isMobile ? 15 : 20,
                marginBottom: isMobile ? 30 : 50,
              }}
            >
              <Flex
                style={{
                  width: isMobile ? "auto" : "100%",
                  overflow: "auto",
                  marginLeft: 50,
                }}
              >
                <Table
                  columns={columns}
                  dataSource={cotasComResumo}
                  rowSelection={rowSelection}
                  onChange={onChange}
                  bordered
                  style={{
                    alignItems: "center",
                    justifyContent: "center",
                    width: isMobile ? "auto" : "100%",
                    maxWidth: isMobile ? "none" : "100%",
                  }}
                  pagination={{ defaultCurrent: currentPage }}
                />
              </Flex>
            </Col>
            <Col span={1}></Col>
          </Row>

          <Modal
            open={isModalExcluirOpen}
            onCancel={fecharModalExcluir}
            onOk={excluirSelecionadas}
            okText="Excluir"
            cancelText="Cancelar"
            confirmLoading={isExcluindo}
            okButtonProps={{ danger: true }}
          >
            <Title level={4}>Confirmar exclusão em lote?</Title>
            <p>
              Deseja excluir {selectedRowKeys.length} cota
              {selectedRowKeys.length > 1 ? "s" : ""} selecionada
              {selectedRowKeys.length > 1 ? "s" : ""}?
            </p>
            <p>Essa acão nao pode ser desfeita.</p>
          </Modal>
          <ModalCompletarCotas
            aptoId={aptoId}
            handleFechar={fecharModalCompletar}
            isModalOpen={isModalCompletarOpen}
            onSuccess={(texto) => {
              showSuccess(texto);
              setTimeout(() => {
                window.location.reload();
              }, 1200);
            }}
            onError={showError}
          />
          <ModalEditar
            isModalOpen={isModalEditarOpen}
            handleFechar={fecharModalEditar}
            cotaId={cotaId}
          />
          <ModalAndamentos
            isModalOpen={isModalAndamentosOpen}
            handleFechar={fecharModalAndamentos}
            cotaId={cotaId}
            numeroCota={numeroCota}
          />
        </Container>
      )}
      ;
    </>
  );
}
